import React from 'react';
import { motion } from 'framer-motion';
import {
  FaHeart,
  FaStar,
  FaGift,
  FaCloud,
  FaDove,
  FaPeace,
  FaHandHoldingHeart,
  BiHeart,
  IoSparkles,
  IoHeart,
  IoBalloon,
  GiPartyPopper,
  GiBalloons,
  GiButterfly,
  GiRose
} from '../../../../icons';

// Floating decorative elements with various animations
const FloatingElement = ({ icon: Icon, delay, duration, xRange, yRange, startPos, color, size }: any) => (
  <motion.div
    style={{
      position: 'absolute',
      ...startPos,
      color: color,
      fontSize: size,
      opacity: 0.12,
      zIndex: 0,
    }}
    animate={{
      x: xRange,
      y: yRange,
      rotate: [0, 10, -10, 0],
      scale: [1, 1.1, 0.9, 1],
    }}
    transition={{
      duration: duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay,
    }}
  >
    <Icon />
  </motion.div>
);

const AuthBackground: React.FC = () => {
  // Check if screen is large enough for animations (performance optimization)
  const [showAnimations, setShowAnimations] = React.useState(true);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setShowAnimations(window.innerWidth >= 970); // Only show on desktop
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Configuration for floating elements - spread across entire screen with celebration & peace theme
  const floatingElements = [
    // Top left area
    { icon: FaHeart, delay: 0, duration: 8, xRange: [0, 15, -10, 0], yRange: [0, -20, -10, 0], startPos: { top: '8%', left: '5%' }, color: '#D7A360', size: '2.5rem' },
    { icon: FaStar, delay: 1, duration: 10, xRange: [0, -20, 15, 0], yRange: [0, 15, -15, 0], startPos: { top: '12%', left: '15%' }, color: '#3957A0', size: '2rem' },
    { icon: GiPartyPopper, delay: 0.5, duration: 9, xRange: [0, 12, -8, 0], yRange: [0, -15, 10, 0], startPos: { top: '5%', left: '25%' }, color: '#D7A360', size: '2.3rem' },

    // Top center
    { icon: FaDove, delay: 2, duration: 14, xRange: [0, -15, 18, 0], yRange: [0, 12, -15, 0], startPos: { top: '8%', left: '45%' }, color: '#3957A0', size: '2.4rem' },
    { icon: GiButterfly, delay: 1.8, duration: 11, xRange: [0, 20, -12, 0], yRange: [0, -18, 15, 0], startPos: { top: '6%', left: '55%' }, color: '#D7A360', size: '2rem' },

    // Top right area
    { icon: IoSparkles, delay: 1.5, duration: 7, xRange: [0, 15, -20, 0], yRange: [0, -25, 15, 0], startPos: { top: '10%', right: '12%' }, color: '#D7A360', size: '2.2rem' },
    { icon: IoBalloon, delay: 2.5, duration: 10, xRange: [0, -10, 15, 0], yRange: [0, -20, 8, 0], startPos: { top: '15%', right: '8%' }, color: '#3957A0', size: '2.5rem' },
    { icon: FaStar, delay: 3, duration: 11, xRange: [0, 20, -15, 0], yRange: [0, -10, 15, 0], startPos: { top: '18%', right: '25%' }, color: '#3957A0', size: '1.8rem' },

    // Middle left
    { icon: FaGift, delay: 2, duration: 12, xRange: [0, 10, -15, 0], yRange: [0, -15, 10, 0], startPos: { top: '40%', left: '3%' }, color: '#D7A360', size: '3rem' },
    { icon: GiRose, delay: 1.3, duration: 13, xRange: [0, -18, 12, 0], yRange: [0, 10, -15, 0], startPos: { top: '35%', left: '10%' }, color: '#D7A360', size: '2.4rem' },
    { icon: IoSparkles, delay: 0.8, duration: 8, xRange: [0, -25, 10, 0], yRange: [0, 15, -20, 0], startPos: { top: '50%', left: '8%' }, color: '#3957A0', size: '2rem' },

    // Middle center
    { icon: FaPeace, delay: 3.2, duration: 15, xRange: [0, 15, -15, 0], yRange: [0, -10, 10, 0], startPos: { top: '48%', left: '48%' }, color: '#3957A0', size: '2.2rem' },
    { icon: FaHandHoldingHeart, delay: 2.8, duration: 12, xRange: [0, -12, 15, 0], yRange: [0, 15, -12, 0], startPos: { top: '52%', left: '55%' }, color: '#D7A360', size: '2.3rem' },

    // Middle right
    { icon: BiHeart, delay: 0.5, duration: 9, xRange: [0, -15, 10, 0], yRange: [0, 20, -10, 0], startPos: { top: '45%', right: '8%' }, color: '#3957A0', size: '2.8rem' },
    { icon: GiBalloons, delay: 1.7, duration: 13, xRange: [0, 18, -15, 0], yRange: [0, -22, 12, 0], startPos: { top: '38%', right: '15%' }, color: '#D7A360', size: '2.6rem' },
    { icon: FaCloud, delay: 2.5, duration: 15, xRange: [0, -10, 20, 0], yRange: [0, 10, -20, 0], startPos: { top: '55%', right: '18%' }, color: '#3957A0', size: '2.5rem' },

    // Bottom left
    { icon: FaStar, delay: 1.8, duration: 9, xRange: [0, -15, 10, 0], yRange: [0, 15, -10, 0], startPos: { bottom: '15%', left: '10%' }, color: '#3957A0', size: '2.3rem' },
    { icon: IoHeart, delay: 2.2, duration: 10, xRange: [0, 12, -18, 0], yRange: [0, -18, 12, 0], startPos: { bottom: '25%', left: '20%' }, color: '#D7A360', size: '2.4rem' },
    { icon: GiButterfly, delay: 3.1, duration: 14, xRange: [0, -20, 10, 0], yRange: [0, 12, -18, 0], startPos: { bottom: '18%', left: '5%' }, color: '#3957A0', size: '2.1rem' },

    // Bottom center
    { icon: IoSparkles, delay: 0.9, duration: 8, xRange: [0, 10, -15, 0], yRange: [0, -12, 15, 0], startPos: { bottom: '12%', left: '48%' }, color: '#D7A360', size: '2rem' },

    // Bottom right
    { icon: IoSparkles, delay: 1.2, duration: 11, xRange: [0, 18, -12, 0], yRange: [0, -15, 18, 0], startPos: { bottom: '20%', right: '15%' }, color: '#3957A0', size: '2.4rem' },
    { icon: FaGift, delay: 2.8, duration: 13, xRange: [0, -12, 15, 0], yRange: [0, 12, -15, 0], startPos: { bottom: '12%', right: '8%' }, color: '#D7A360', size: '2.6rem' },
    { icon: GiPartyPopper, delay: 2.3, duration: 12, xRange: [0, 15, -10, 0], yRange: [0, -16, 12, 0], startPos: { bottom: '22%', right: '25%' }, color: '#3957A0', size: '2.2rem' },
  ];

  if (!showAnimations) return null;

  return (
    <>
      {/* Full-screen fixed background container */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Floating background elements */}
        {floatingElements.map((element, index) => (
          <FloatingElement key={index} {...element} />
        ))}

        {/* Elegant gradient background overlay with shimmer */}
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 30%, rgba(57, 87, 160, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(215, 163, 96, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 60%),
              linear-gradient(135deg, rgba(215, 163, 96, 0.02) 0%, rgba(57, 87, 160, 0.02) 100%)
            `,
            zIndex: 0,
            pointerEvents: 'none'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
        />

        {/* Elegant shine effect - diagonal light rays */}
        <motion.div
          style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            right: '-50%',
            bottom: '-50%',
            background: `
              linear-gradient(
                120deg,
                transparent 0%,
                transparent 40%,
                rgba(255, 255, 255, 0.05) 45%,
                rgba(255, 255, 255, 0.1) 50%,
                rgba(255, 255, 255, 0.05) 55%,
                transparent 60%,
                transparent 100%
              )
            `,
            zIndex: 1,
            pointerEvents: 'none',
            transformOrigin: 'center'
          }}
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        {/* Subtle shimmer particles */}
        {[...Array(8)].map((_, index) => (
          <motion.div
            key={`shimmer-${index}`}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%)',
              boxShadow: '0 0 10px rgba(255, 255, 255, 0.6)',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              zIndex: 2,
              pointerEvents: 'none'
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1.5, 0.5],
              y: [0, -30, 0]
            }}
            transition={{
              duration: 3 + index * 0.5,
              repeat: Infinity,
              delay: index * 0.8,
              ease: "easeInOut"
            }}
          />
        ))}

        {/* Elegant animated gradient orbs with glow - spread across screen */}
        <motion.div
          style={{
            position: 'absolute',
            top: '15%',
            left: '15%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(215, 163, 96, 0.12) 0%, rgba(215, 163, 96, 0.04) 50%, transparent 70%)',
            filter: 'blur(60px)',
            boxShadow: '0 0 80px rgba(215, 163, 96, 0.15)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.4, 0.7, 0.4],
            x: [0, 40, 0],
            y: [0, -40, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          style={{
            position: 'absolute',
            top: '60%',
            right: '20%',
            width: '220px',
            height: '220px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(57, 87, 160, 0.12) 0%, rgba(57, 87, 160, 0.04) 50%, transparent 70%)',
            filter: 'blur(55px)',
            boxShadow: '0 0 70px rgba(57, 87, 160, 0.15)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            scale: [1, 1.4, 1],
            opacity: [0.4, 0.8, 0.4],
            x: [0, -35, 0],
            y: [0, 35, 0]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        <motion.div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '25%',
            width: '190px',
            height: '190px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(215, 163, 96, 0.10) 0%, rgba(215, 163, 96, 0.03) 50%, transparent 70%)',
            filter: 'blur(50px)',
            boxShadow: '0 0 60px rgba(215, 163, 96, 0.12)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            scale: [1, 1.25, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 30, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />

        {/* Additional elegant glow orbs for depth */}
        <motion.div
          style={{
            position: 'absolute',
            top: '30%',
            right: '35%',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 70%)',
            filter: 'blur(45px)',
            boxShadow: '0 0 50px rgba(255, 255, 255, 0.1)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.5, 0.2],
            x: [0, -20, 0],
            y: [0, 20, 0]
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
        />

        <motion.div
          style={{
            position: 'absolute',
            bottom: '35%',
            right: '10%',
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(57, 87, 160, 0.10) 0%, transparent 70%)',
            filter: 'blur(40px)',
            boxShadow: '0 0 55px rgba(57, 87, 160, 0.12)',
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.6, 0.3],
            x: [0, 25, 0],
            y: [0, -25, 0]
          }}
          transition={{
            duration: 13,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2.5
          }}
        />
      </motion.div>
    </>
  );
};

export default AuthBackground;
